import React, { useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Badge, InputRef } from 'antd';
import { Button, Input, Space } from 'antd';
import type { ColumnType } from 'antd/es/table';
import type { ColumnGroupType, FilterConfirmProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import moment from "moment";
//import prettyBytes from 'pretty-bytes';
import { ExportTableButton, Table } from "ant-table-extensions";
import { DataType, getClientData } from '../../api/client';
import useGetData from "../../hooks/useGetData";
import { FileExcelOutlined } from '@ant-design/icons';

import { humanFileSize } from '../../services/utils';

type DataIndex = keyof DataType;

const ClientsPage: React.FC = () => {

    const { data, isLoading, isFetching, isError, error } = useGetData('clients', getClientData);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const [dataClient, setDataClient] = React.useState<DataType[] | undefined>();


    React.useEffect(() => {
        if (isError) {
            console.log('clients_errors', error);
        }
        if ((!isLoading || !isFetching) && !isError && data) {
            setDataClient(data);
        }
    }, [isLoading, isFetching, isError, data, error]);


    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);

                        }}
                        style={{ color: '#ff8300' }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                        style={{ color: '#ff8300' }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },

        render: (text) =>
            searchedColumn !== dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const columns: (ColumnType<DataType> | ColumnGroupType<DataType>)[] = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            ...getColumnSearchProps('name'),
            sorter: (a, b) => a.name.length - b.name.length,
            sortDirections: ['descend', 'ascend'],
        },

        {
            title: 'NetWork',
            dataIndex: 'wirelessNetworkName',
            key: 'wirelessNetworkName',
            ...getColumnSearchProps('wirelessNetworkName'),
            sorter: (a, b) => a.wirelessNetworkName.length - b.wirelessNetworkName.length,
            sortDirections: ['descend', 'ascend'],
        },

        {
            title: 'Duration',
            dataIndex: 'connectionDurationInSeconds',
            key: 'connectionDurationInSeconds',
            ...getColumnSearchProps('connectionDurationInSeconds'),
            sorter: (a, b) => a.connectionDurationInSeconds - b.connectionDurationInSeconds,
            sortDirections: ['descend', 'ascend'],
            render: (number) => moment.duration(number, 'seconds').humanize()
        },

        {
            title: 'Connection Health',
            dataIndex: 'health',
            key: 'health',
            ...getColumnSearchProps('health'),
            sorter: (a, b) => a.health.length - b.health.length,
            sortDirections: ['descend', 'ascend'],
            render: (text) => <Badge color={text === 'Good' ? 'red' : 'green'} text={text} />
        },

        {
            title: 'Downloading',
            dataIndex: 'downstreamThroughputInBitsPerSecond',
            key: 'downstreamThroughputInBitsPerSecond',
            ...getColumnSearchProps('downstreamThroughputInBitsPerSecond'),
            sorter: (a, b) => a.downstreamThroughputInBitsPerSecond - b.downstreamThroughputInBitsPerSecond,
            sortDirections: ['descend', 'ascend'],
            render: (number) => humanFileSize(number)
        },

        {
            title: 'Uploading',
            dataIndex: 'upstreamThroughputInBitsPerSecond',
            key: 'upstreamThroughputInBitsPerSecond',
            ...getColumnSearchProps('upstreamThroughputInBitsPerSecond'),
            sorter: (a, b) => a.upstreamThroughputInBitsPerSecond - b.upstreamThroughputInBitsPerSecond,
            sortDirections: ['descend', 'ascend'],
            render: (number) => humanFileSize(number)
        },

        {
            title: 'Transferred',
            dataIndex: 'streamDataTransferredInBytes',
            key: 'streamDataTransferredInBytes',
            ...getColumnSearchProps('streamDataTransferredInBytes'),
            sorter: (a, b) => a.streamDataTransferredInBytes - b.streamDataTransferredInBytes,
            sortDirections: ['descend', 'ascend'],
            render: (number) => humanFileSize(number)
        },

        {
            title: 'Top Application Category',
            dataIndex: 'topApplicationCategory',
            key: 'topApplicationCategory',
            ...getColumnSearchProps('topApplicationCategory'),
            sorter: (a, b) => a.topApplicationCategory.length - b.topApplicationCategory.length,
            sortDirections: ['descend', 'ascend'],
            render: (text) => text === 'unknown' ? 'Uncategorized' : text,
        },

    ]

    return (
        <>
            <ExportTableButton
                fileName='Clients'
                dataSource={dataClient}
                columns={columns}
                btnProps={{ type: "primary", icon: <FileExcelOutlined /> }}
                showColumnPicker
            >
                Export
            </ExportTableButton>
            <Table rowKey="name" columns={columns} dataSource={dataClient} scroll={{ x: 1500 }} loading={isLoading || isFetching} />
        </>
    )
}

export default ClientsPage;